import React, { useState, useEffect } from "react";
import {
  Loader2, Mail, Plus, Edit2, Eye, EyeOff, CheckCircle2, Trash2, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "#FF4D00";

const HUB_OPTIONS = [
  { value: "carrier",   label: "Carrier",   color: "#3B82F6" },
  { value: "recruiter", label: "Recruiter", color: "#10B981" },
  { value: "safety",    label: "Safety",    color: "#F59E0B" },
  { value: "admin",     label: "Admin",     color: "#EF4444" },
];

const BLANK_FORM = { email: "", password: "", hub: "recruiter", name: "", phone: "", carrier_name: "", carrier_email: "" };

export function EmailAccountsPanel({ token }) {
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(BLANK_FORM);
  const [saving, setSaving]       = useState(false);
  const [showPw, setShowPw]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [newPwEdit, setNewPwEdit] = useState("");
  const [updatingSingle, setUpdatingSingle] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.marketAdminListAccounts(token);
      setAccounts(r.accounts || []);
    } catch { toast.error("Failed to load accounts"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function createAccount(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.marketAdminCreateAccount(token, form);
      toast.success(`Account created for ${form.email}`);
      setShowForm(false);
      setForm(BLANK_FORM);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  }

  async function saveEdit(accountId) {
    setUpdatingSingle(accountId);
    const payload = { ...editForm };
    if (newPwEdit) payload.new_password = newPwEdit;
    try {
      await api.marketAdminUpdateAccount(token, accountId, payload);
      toast.success("Account updated");
      setEditing(null);
      setNewPwEdit("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setUpdatingSingle(null); }
  }

  async function toggleActive(acct) {
    try {
      await api.marketAdminUpdateAccount(token, acct.account_id, { is_active: !acct.is_active });
      toast.success(acct.is_active ? "Account deactivated" : "Account activated");
      load();
    } catch { toast.error("Update failed"); }
  }

  async function deleteAccount(accountId, email) {
    if (!window.confirm(`Delete account for ${email}? This cannot be undone.`)) return;
    try {
      await api.marketAdminDeleteAccount(token, accountId);
      toast.success("Account deleted");
      load();
    } catch { toast.error("Delete failed"); }
  }

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inp = "w-full text-sm px-3 py-2 focus:outline-none bg-[#0f0f0f] border border-white/10 text-white focus:border-[#FF4D00] transition-colors";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-0.5">Admin</p>
          <h3 className="font-head text-xl font-black uppercase tracking-[0.04em]" style={{ color: ACCENT }}>Email Accounts</h3>
          <p className="text-xs text-white/30 mt-0.5">Invite-only email+password logins for hub access.</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} data-testid="create-account-btn"
          className="flex items-center gap-2 px-4 py-2 font-head text-xs uppercase tracking-[0.15em] font-black transition-all hover:brightness-110"
          style={{ background: showForm ? "rgba(255,255,255,0.06)" : ACCENT, color: "white" }}>
          {showForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          {showForm ? "Cancel" : "Create Account"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={createAccount} className="space-y-3 p-4" style={{ background: "#161618", border: "1px solid rgba(255,77,0,0.25)" }}>
          <p className="font-head text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">New Account</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[9px] font-head uppercase tracking-[0.2em] text-white/40 mb-1">Full Name *</label>
              <input value={form.name} onChange={setF("name")} placeholder="John Doe" required className={inp} />
            </div>
            <div>
              <label className="block text-[9px] font-head uppercase tracking-[0.2em] text-white/40 mb-1">Hub Role *</label>
              <select value={form.hub} onChange={setF("hub")} required className={inp}>
                {HUB_OPTIONS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[9px] font-head uppercase tracking-[0.2em] text-white/40 mb-1">Email Address *</label>
              <input type="email" value={form.email} onChange={setF("email")} placeholder="user@company.com" required className={inp} />
            </div>
            <div>
              <label className="block text-[9px] font-head uppercase tracking-[0.2em] text-white/40 mb-1">Password * (min 8 chars)</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={form.password} onChange={setF("password")}
                  placeholder="••••••••" required minLength={8} className={`${inp} pr-9`} />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                  {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>
          {form.hub === "carrier" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] font-head uppercase tracking-[0.2em] text-white/40 mb-1">Company Name</label>
                <input value={form.carrier_name} onChange={setF("carrier_name")} placeholder="ABC Trucking LLC" className={inp} />
              </div>
              <div>
                <label className="block text-[9px] font-head uppercase tracking-[0.2em] text-white/40 mb-1">Contact Phone</label>
                <input value={form.phone} onChange={setF("phone")} placeholder="(555) 000-0000" className={inp} />
              </div>
            </div>
          )}
          <button type="submit" disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 font-head text-xs uppercase tracking-[0.15em] font-black disabled:opacity-50"
            style={{ background: ACCENT, color: "white" }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Create Account
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} /></div>
      ) : accounts.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-center" style={{ border: "1px dashed rgba(255,255,255,0.08)" }}>
          <Mail className="w-8 h-8 mb-2 opacity-20" />
          <p className="text-xs text-white/25 font-head uppercase tracking-[0.2em]">No email accounts yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map((acct) => {
            const hubMeta = HUB_OPTIONS.find((h) => h.value === acct.hub) || HUB_OPTIONS[0];
            const isEditingThis = editing === acct.account_id;
            return (
              <div key={acct.account_id} style={{ background: "#161618", border: "1px solid rgba(255,255,255,0.07)" }}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="w-8 h-8 shrink-0 flex items-center justify-center font-head text-xs font-black"
                    style={{ background: `${hubMeta.color}15`, border: `1px solid ${hubMeta.color}30`, color: hubMeta.color }}>
                    {(acct.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-head text-sm font-bold text-white truncate">{acct.name}</p>
                      <span className="text-[9px] font-head uppercase tracking-[0.12em] px-1.5 py-0.5"
                        style={{ background: `${hubMeta.color}18`, color: hubMeta.color, border: `1px solid ${hubMeta.color}30` }}>
                        {acct.hub}
                      </span>
                      {!acct.is_active && (
                        <span className="text-[9px] font-head uppercase tracking-[0.1em] px-1.5 py-0.5 text-white/30"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-white/40 truncate">{acct.email}</p>
                    {acct.last_login && <p className="text-[9px] text-white/20">Last login: {new Date(acct.last_login).toLocaleDateString()}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => { setEditing(isEditingThis ? null : acct.account_id); setEditForm({ name: acct.name, hub: acct.hub }); setNewPwEdit(""); }}
                      className="p-1.5 transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.3)" }}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => toggleActive(acct)}
                      className="p-1.5 transition-colors" style={{ color: acct.is_active ? "#4ADE80" : "rgba(255,255,255,0.2)" }}>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteAccount(acct.account_id, acct.email)}
                      className="p-1.5 transition-colors hover:text-red-400" style={{ color: "rgba(255,255,255,0.2)" }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {isEditingThis && (
                  <div className="px-4 pb-3 pt-1 space-y-2" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[9px] font-head uppercase tracking-[0.15em] text-white/30 mb-1">Name</label>
                        <input value={editForm.name || ""} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className={inp} />
                      </div>
                      <div>
                        <label className="block text-[9px] font-head uppercase tracking-[0.15em] text-white/30 mb-1">Hub</label>
                        <select value={editForm.hub || ""} onChange={(e) => setEditForm((f) => ({ ...f, hub: e.target.value }))} className={inp}>
                          {HUB_OPTIONS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[9px] font-head uppercase tracking-[0.15em] text-white/30 mb-1">New Password (leave blank to keep current)</label>
                      <input type="password" value={newPwEdit} onChange={(e) => setNewPwEdit(e.target.value)} placeholder="••••••••" minLength={8} className={inp} />
                    </div>
                    <button onClick={() => saveEdit(acct.account_id)} disabled={updatingSingle === acct.account_id}
                      className="flex items-center gap-1.5 px-3 py-1.5 font-head text-xs uppercase tracking-[0.12em] disabled:opacity-50"
                      style={{ background: "rgba(255,77,0,0.15)", color: ACCENT, border: "1px solid rgba(255,77,0,0.3)" }}>
                      {updatingSingle === acct.account_id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                      Save Changes
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
